import { useState } from "react"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import Dialog from "@/v2-ui/dialog"
import Label from "@/v2-ui/label"
import Select from "@/v2-ui/select"
import Textarea from "@/v2-ui/textarea"
import { toast } from "@/v2-ui/toast"
import Button from "@/v2-backoffice-client/components/button"
import LicensePlanTargetSelect from "@/v2-backoffice-client/license/plan/LicensePlanTargetSelect"
import { useLicensePlanDeprecationSetMutation } from "@/v2-backoffice-client/license/plan/deprecate/license.plan.deprecation.set.mutation"
import { evictLicenseQueries } from "@/v2-backoffice-client/license/license.utils"
import type { LicensePlanFragment } from "@/v2-backoffice-client/license/plan/__types__/license.plan.fragment"

// Radix Select items can't carry an empty-string value, so "no successor"
// gets a sentinel that is translated to null on submit.
const NO_SUCCESSOR_VALUE = "0"

export type LicensePlanDeprecateDialogProps = {
  plan: LicensePlanFragment
  open: boolean
  onOpenChange(open: boolean): void
}

// Declares (or lifts) the deprecation of a license: "we are moving customers off
// this". Deprecating opens the license panel's Migration tab as the worklist;
// the suggested successor and note follow the license name around the app so
// sales/CSM see WHAT to offer instead wherever they meet the license. Fenerum
// is deliberately untouched: deactivating the license's terms there (which stops
// NEW subscriptions) stays a manual Fenerum decision, and the actual per-
// customer license changes are executed there too.
function LicensePlanDeprecateDialog(props: LicensePlanDeprecateDialogProps) {
  const { plan, open, onOpenChange } = props
  const isDeprecated = Boolean(plan.DeprecatedAt)

  const [successorPlanId, setSuccessorPlanId] = useState(
    plan.SuccessorPlanId ? String(plan.SuccessorPlanId) : NO_SUCCESSOR_VALUE,
  )
  const [note, setNote] = useState(plan.DeprecationNote ?? "")

  // The licenses board and customer panels badge deprecated license names from
  // their own read (PlanDeprecatedAt on getLicenses), so those get evicted;
  // the license row itself updates in place from the returned fragment.
  const [setDeprecation, { loading, error }] =
    useLicensePlanDeprecationSetMutation({
      update: evictLicenseQueries,
      onCompleted(data) {
        const updated = data.setLicensePlanDeprecation
        toast({
          variant: "success",
          title: updated.DeprecatedAt
            ? "License deprecated"
            : "License reactivated",
          description: updated.DeprecatedAt
            ? `${updated.Name} is now marked deprecated everywhere it shows. Work the migration from the license panel's Migration tab.`
            : `${updated.Name} is a live license again.`,
        })
        onOpenChange(false)
      },
    })

  function submit(nextIsDeprecated: boolean) {
    void setDeprecation({
      variables: {
        planId: plan.Id,
        input: {
          IsDeprecated: nextIsDeprecated,
          SuccessorPlanId:
            nextIsDeprecated && successorPlanId !== NO_SUCCESSOR_VALUE
              ? Number(successorPlanId)
              : null,
          Note: nextIsDeprecated && note.trim() ? note.trim() : null,
        },
      },
    })
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content align="top">
        <Dialog.Header>
          <Dialog.Title>
            {isDeprecated ? "Deprecation — " : "Deprecate "}
            {plan.Name}
          </Dialog.Title>
          <Dialog.Description>
            Deprecating marks the license everywhere it shows and opens a
            migration worklist on the license panel — customers keep billing
            until each one is renegotiated and the change is executed in
            Fenerum. Nothing changes for customers by itself.
          </Dialog.Description>
        </Dialog.Header>

        <ErrorMessage className="my-4 text-center" error={error} />

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Suggested successor</Label>
            <LicensePlanTargetSelect
              value={successorPlanId}
              onValueChange={setSuccessorPlanId}
              excludePlanId={plan.Id}
              placeholder="Which license should customers move to?"
            >
              <Select.Item value={NO_SUCCESSOR_VALUE}>
                No successor — decided per customer
              </Select.Item>
            </LicensePlanTargetSelect>
            <p className="text-xs text-muted-foreground">
              The default offer. Individual migrations can still agree on a
              different license.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Note</Label>
            <Textarea
              value={note}
              rows={3}
              placeholder="Why it's being retired, and what to offer instead..."
              onChange={(event) => {
                setNote(event.target.value)
              }}
            />
            <p className="text-xs text-muted-foreground">
              Shown wherever the deprecated badge appears — write it for the
              colleague who meets this license on a customer.
            </p>
          </div>
        </div>

        <Dialog.Footer className="mt-6 flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              onOpenChange(false)
            }}
          >
            Cancel
          </Button>

          {isDeprecated && (
            <Button
              type="button"
              variant="outline"
              isLoading={loading}
              data-feature-code="UI_BackofficeLicensePlan_Deprecation_Reactivate"
              onClick={() => {
                submit(false)
              }}
            >
              Reactivate License
            </Button>
          )}

          <Button
            type="button"
            variant={isDeprecated ? "primary" : "destructive"}
            isLoading={loading}
            data-feature-code="UI_BackofficeLicensePlan_Deprecation_Save"
            onClick={() => {
              submit(true)
            }}
          >
            {loading
              ? "Saving..."
              : isDeprecated
                ? "Save Changes"
                : "Deprecate License"}
          </Button>
        </Dialog.Footer>
      </Dialog.Content>
    </Dialog>
  )
}

export default LicensePlanDeprecateDialog
